import { OWNER_NAME, OWNER_PHONE } from './identity';
import type {
  AircraftDoc,
  BookingDoc,
  DocumentDoc,
  FlightDoc,
  MaintenanceItemDoc,
  ReadingDiscrepancyDoc,
  SquawkDoc,
  TransactionDoc,
  UserDoc,
} from './types';

const now = new Date();
function day(offset: number, hour = 9, min = 0) {
  const d = new Date(now);
  d.setDate(d.getDate() + offset);
  d.setHours(hour, min, 0, 0);
  return d;
}
function iso(d: Date) {
  return d.toISOString().slice(0, 10);
}
function ts(d: Date) {
  return { toDate: () => d, seconds: Math.floor(d.getTime() / 1000), nanoseconds: 0 } as never;
}

// ────────────────────────────── Demo UIDs ──────────────────────────────
export const DEMO_UIDS = {
  admin: 'demo-admin',
  lesseeMe: 'demo-lessee_active',
  brian: 'demo-lessee-brian',
  jordan: 'demo-lessee-jordan',
  morgan: 'demo-lessee-morgan',
  cfi: 'demo-cfi',
} as const;

// ────────────────────────────── Maintenance ──────────────────────────────
// Mirrors the real N123AB items. Hours-based items track on TACH (currentTach
// 1037.3); dueHobbs/lastDoneHobbs hold tach readings, so seat rail 1110.3,
// oil 1091.0, ignition 2061.0 read ~73 / ~54 / ~1024 hr to next.
export const DEMO_MAINTENANCE: MaintenanceItemDoc[] = [
  { itemId: 'mx-annual', aircraftId: 'N123AB', label: 'Annual Inspection', track: 'date', dueDate: '2026-09-30', intervalDays: 365, lastDoneDate: '2025-09-30', isAirworthiness: true, lastAlertLevel: 0, createdAt: ts(day(-260)) },
  { itemId: 'mx-seatrail', aircraftId: 'N123AB', label: 'Seat Rail Inspection (AD)', track: 'hours', dueHobbs: 1110.3, intervalHours: 100, lastDoneHobbs: 1010.3, isAirworthiness: true, lastAlertLevel: 0, createdAt: ts(day(-120)) },
  { itemId: 'mx-oil', aircraftId: 'N123AB', label: 'Oil Change', track: 'both', dueHobbs: 1091.0, dueDate: '2026-09-30', intervalHours: 100, intervalDays: 365, lastDoneHobbs: 991.0, lastDoneDate: '2026-03-26', isAirworthiness: false, deferredUntil: '2026-09-30', deferReason: 'doing it at the annual', notes: 'Sample Aviation Services (XX0R000X) · W/O 123AB032626 · 03-26-2026 @ 991.0 tach. Drained & strained oil per Continental CSB07-01; new Tempest AA48108-2 filter; serviced with 8 qt Phillips 100AW; C/W SB-001 Rev C; complied with AD 2023-17-04. Also replaced leaking oil cooler adaptor O-ring (MS28775-229) and tightened Cyl #1 rocker cover.', docUrl: undefined, lastAlertLevel: 0, createdAt: ts(day(-71)) },
  { itemId: 'mx-ignition', aircraftId: 'N123AB', label: 'Ignition Inspection', track: 'hours', dueHobbs: 2061.0, isAirworthiness: false, lastAlertLevel: 0, createdAt: ts(day(-30)) },
  { itemId: 'mx-altimeter', aircraftId: 'N123AB', label: 'Altimeter / Static (91.411)', track: 'date', dueDate: '2028-01-31', intervalDays: 730, lastDoneDate: '2026-01-09', isAirworthiness: true, lastAlertLevel: 0, createdAt: ts(day(-140)) },
  { itemId: 'mx-transponder', aircraftId: 'N123AB', label: 'Transponder (91.413)', track: 'date', dueDate: '2028-01-31', intervalDays: 730, lastDoneDate: '2026-01-09', isAirworthiness: true, lastAlertLevel: 0, createdAt: ts(day(-140)) },
];

// ────────────────────────────── Aircraft ──────────────────────────────
export const DEMO_AIRCRAFT: AircraftDoc[] = [
  {
    weightBalance: {
      emptyWeight: 1436,
      emptyCg: 38.46,
      emptyMoment: 55224,
      usefulLoad: 814,
      maxGrossWeight: 2250,
      weighedBy: 'Pabas Avionics · FAA Repair Station BV3R411L',
    },
    tcds: {
      vSpeedsKcas: { maneuvering: 100, maxStructuralCruise: 122, neverExceed: 139, flapsExtended: 87 },
      minFuelGrade: '80/87 minimum grade aviation gasoline',
      cgEnvelope: {
        normal: [
          { weight: 1850, fwd: 36.4, aft: 46.4 },
          { weight: 2250, fwd: 40.5, aft: 46.4 },
        ],
        utility: [
          { weight: 1850, fwd: 36.4, aft: 40.3 },
          { weight: 1950, fwd: 37.4, aft: 40.3 },
        ],
      },
    },
    aircraftId: 'N123AB',
    tailNumber: 'N123AB',
    make: 'Cessna',
    model: '172C',
    year: 1961,
    serialNumber: '17255012',
    engineMake: 'Continental',
    engineModel: 'O-300-D',
    engineSerial: '3588-9-7-D',
    homeBase: 'KLGB',
    hourlyRate: 100,
    cfiDiscountRate: 75, // promo plan rate, $/Hobbs-hr (field name kept; means promo rate)
    awayFromBaseHoursPerDay: 2,
    annualDueDate: '2026-09-30',
    fuelBillingEnabled: false, // fuel pass-through removed — pilots fuel directly
    currentHobbs: 5586.5,
    currentTach: 1039.7,
    oilSpec: 'Phillips 66 Victory 100AW (SAE 50) · 8 qt · Tempest AA48108-2 filter',
    insuredValue: 122000,
    // Hobbs/tach integrity audit trail — every admin reading change is recorded
    // (who / when / why). One sample entry so demo tours render the trail.
    readingAudit: [
      {
        at: day(-71, 14, 0).toISOString(),
        byUid: DEMO_UIDS.admin,
        byName: OWNER_NAME,
        field: 'tach',
        fromTach: 1037.3,
        toTach: 1039.7,
        reason: 'Post-maintenance reading update',
      },
    ],
    status: 'active',
  },
];

// ─────────────────── Hobbs/tach integrity (reading discrepancies) ──────────────
// Server-written records (onFlightLogged) when a logged flight's observed start
// didn't match the aircraft's expected reading — someone before forgot to log.
// One sample so demo tours of the integrity surface don't crash.
export const DEMO_READING_DISCREPANCIES: ReadingDiscrepancyDoc[] = [
  {
    discrepancyId: 'rd-demo-1',
    aircraftId: 'N123AB',
    tailNumber: 'N123AB',
    field: 'hobbs',
    expected: 5584.2,
    observed: 5586.5,
    delta: 2.3,
    flightId: 'f-demo-1',
    byUid: DEMO_UIDS.brian,
    byName: 'Brian Matthews',
    ts: ts(day(-9, 16, 30)),
    resolved: false,
  },
];

// ────────────────────────────── Lessees ──────────────────────────────
export const DEMO_LESSEES: UserDoc[] = [
  {
    uid: DEMO_UIDS.brian,
    email: 'brian.matthews@example.com',
    role: 'lessee',
    status: 'active',
    createdAt: ts(day(-72)),
    approvedAt: ts(day(-70)),
    // Fully onboarded: lease executed and recorded. Makes the "you're all set"
    // email button reachable in demo, which is otherwise an unverifiable path.
    leaseSignedAt: ts(day(-68)),
    leaseSignedVersion: 'ajm-dry-lease-v13-2026-09',
    personal: {
      firstName: 'Brian',
      lastName: 'Matthews',
      phone: '(562) 555-0148',
      dateOfBirth: '1984-03-12',
      address: '218 Marina Dr, Seal Beach, CA 90740',
    },
    pilot: {
      certificateType: 'Private',
      certificateNumber: '4192847',
      ratings: ['Single-Engine Land', 'Instrument Airplane'],
      totalHours: 612,
      hoursIn172: 88,
      hoursLast90: 14,
      hoursLast12Months: 52,
      medicalClass: '3rd',
      medicalExpiration: iso(day(218)),
      bfrExpiration: iso(day(312)),
    },
  },
  {
    uid: DEMO_UIDS.jordan,
    email: 'jordan.lee@example.com',
    role: 'lessee',
    status: 'active',
    createdAt: ts(day(-44)),
    approvedAt: ts(day(-42)),
    personal: {
      firstName: 'Jordan',
      lastName: 'Lee',
      phone: '(714) 555-0199',
    },
    pilot: {
      certificateType: 'Commercial',
      certificateNumber: '3782104',
      ratings: ['Single-Engine Land', 'Instrument Airplane', 'CFI'],
      totalHours: 1240,
      hoursIn172: 412,
      hoursLast90: 38,
      hoursLast12Months: 184,
      medicalClass: '2nd',
      medicalExpiration: iso(day(91)),
      bfrExpiration: iso(day(421)),
    },
  },
  {
    uid: DEMO_UIDS.morgan,
    email: 'morgan.flynn@example.com',
    role: 'lessee',
    status: 'verification_pending',
    createdAt: ts(day(-4)),
    approvedAt: ts(day(-3)),
    personal: {
      firstName: 'Morgan',
      lastName: 'Flynn',
      phone: '(310) 555-0142',
    },
    pilot: {
      certificateType: 'Private',
      ratings: ['Single-Engine Land'],
      totalHours: 178,
      hoursIn172: 12,
    },
  },
  {
    uid: 'demo-lessee-pending-1',
    email: 'taylor.reed@example.com',
    role: 'lessee',
    status: 'pending_approval',
    createdAt: ts(day(-1)),
    personal: { firstName: 'Taylor', lastName: 'Reed', phone: '(562) 555-0102' },
  },
];

// ────────────────────────────── Bookings ──────────────────────────────
// Contact info denormalized onto each booking so pilots can call each other
// to coordinate (running behind, etc.).
const BOOKING_CONTACTS: Record<string, { name: string; phone: string }> = {
  [DEMO_UIDS.admin]: { name: OWNER_NAME, phone: OWNER_PHONE },
  [DEMO_UIDS.brian]: { name: 'Brian Matthews', phone: '(562) 555-0148' },
  [DEMO_UIDS.jordan]: { name: 'Jordan Lee', phone: '(714) 555-0199' },
  [DEMO_UIDS.morgan]: { name: 'Morgan Flynn', phone: '(310) 555-0142' },
  [DEMO_UIDS.cfi]: { name: 'Taylor Brooks', phone: '562-555-0147' },
};
const RAW_BOOKINGS: BookingDoc[] = [
  {
    // The owner's own slot. Cancelling this must NOT show the operational-control
    // reason gate — that guard is for cancelling someone else's reservation.
    bookingId: 'b-000',
    aircraftId: 'N123AB',
    lesseeUid: DEMO_UIDS.admin,
    lesseeName: OWNER_NAME,
    startDateTime: ts(day(3, 9, 0)),
    endDateTime: ts(day(3, 12, 0)),
    status: 'auto_confirmed',
    expectedDestinations: 'KLGB local',
    createdAt: ts(day(-3)),
  },
  {
    bookingId: 'b-001',
    aircraftId: 'N123AB',
    lesseeUid: DEMO_UIDS.brian,
    startDateTime: ts(day(1, 8, 0)),
    endDateTime: ts(day(1, 11, 30)),
    status: 'approved',
    expectedDestinations: 'KLGB → KCMA → KLGB',
    createdAt: ts(day(-2)),
  },
  {
    bookingId: 'b-002',
    aircraftId: 'N123AB',
    lesseeUid: DEMO_UIDS.jordan,
    startDateTime: ts(day(2, 14, 0)),
    endDateTime: ts(day(2, 17, 0)),
    status: 'auto_confirmed',
    expectedDestinations: 'KLGB local',
    createdAt: ts(day(-1)),
  },
  {
    bookingId: 'b-003',
    aircraftId: 'N123AB',
    lesseeUid: DEMO_UIDS.brian,
    startDateTime: ts(day(5, 7, 0)),
    endDateTime: ts(day(7, 18, 0)),
    status: 'pending_approval',
    expectedDestinations: 'KLGB → KSBP → KMRY → KLGB (overnight)',
    notes: 'Weekend trip — staying at a friend’s hangar at Monterey.',
    createdAt: ts(day(0, 18, 0)),
  },
  {
    bookingId: 'b-004',
    aircraftId: 'N123AB',
    lesseeUid: DEMO_UIDS.jordan,
    startDateTime: ts(day(10, 9, 0)),
    endDateTime: ts(day(10, 13, 0)),
    status: 'approved',
    expectedDestinations: 'KLGB local pattern work',
    createdAt: ts(day(-3)),
  },
  {
    bookingId: 'b-005',
    aircraftId: 'N123AB',
    lesseeUid: DEMO_UIDS.brian,
    startDateTime: ts(day(-3, 9, 0)),
    endDateTime: ts(day(-3, 12, 0)),
    status: 'completed',
    expectedDestinations: 'KLGB → KCRQ → KLGB',
    createdAt: ts(day(-6)),
  },
  {
    // The demo CFI's discovery flight with a prospect (demo 'cfi' seat). No waiverId:
    // the guest waiver in demo stamps it on this object, the way the server trigger
    // stamps the real booking.
    bookingId: 'b-disc-1',
    aircraftId: 'N123AB',
    lesseeUid: DEMO_UIDS.cfi,
    startDateTime: ts(day(0, 10, 0)),
    endDateTime: ts(day(0, 11, 30)),
    status: 'auto_confirmed',
    expectedDestinations: 'KLGB local',
    flightPurpose: 'discovery',
    discoveryGuest: 'Jordan Rivera',
    createdAt: ts(day(-1)),
  },
];

export const DEMO_BOOKINGS: BookingDoc[] = RAW_BOOKINGS.map((b) => ({
  ...b,
  lesseeName: BOOKING_CONTACTS[b.lesseeUid]?.name,
  lesseePhone: BOOKING_CONTACTS[b.lesseeUid]?.phone,
}));

// ────────────────────────────── Flights ──────────────────────────────
// ────────────────────────── Discovery flight requests ──────────────────────────
// One request from the LGB61 site, made through the demo CFI's personal link, so it is
// assigned to the demo CFI seat (Casey 10/20: no first-come claiming).
export const DEMO_DISCOVERY_REQUESTS = [
  {
    id: 'dr-demo-1',
    name: 'Jordan Rivera',
    email: 'jordan.rivera@example.com',
    phone: '(562) 555-0123',
    slotStart: day(0, 10, 0).toISOString(),
    slotEnd: day(0, 11, 30).toISOString(),
    passengers: 'Just me',
    experience: 'Never',
    weight: '165',
    smsConsent: true,
    status: 'claimed' as const,
    claimedByUid: DEMO_UIDS.cfi,
    claimedByName: 'Taylor Brooks',
    assignedBy: 'cfi-link' as const,
    slotOpenAtRequest: true,
    createdAt: ts(day(-1, 19, 12)),
  },
];

export const DEMO_FLIGHTS: FlightDoc[] = [
  {
    flightId: 'f-001',
    aircraftId: 'N123AB',
    lesseeUid: DEMO_UIDS.brian,
    date: iso(day(-3)),
    hobbsOut: 4215.3,
    hobbsIn: 4218.6,
    tachOut: 3984.4,
    tachIn: 3987.4,
    hoursFlown: 3.3,
    departureAirport: 'KLGB',
    destinations: ['KCRQ', 'KLGB'],
    passengerCount: 1,
    fuelLevelOnReturn: '1/2',
    notes: 'Smooth flight. Topped off at Palomar.',
    baseCharge: 330,
    awayFromBaseCharge: 0,
    totalCharge: 330,
    stripeChargeId: 'ch_demo_001',
    createdAt: ts(day(-3, 13, 0)),
  },
  {
    flightId: 'f-002',
    aircraftId: 'N123AB',
    lesseeUid: DEMO_UIDS.jordan,
    date: iso(day(-7)),
    hobbsOut: 4213.5,
    hobbsIn: 4215.3,
    tachOut: 3982.7,
    tachIn: 3984.4,
    hoursFlown: 1.8,
    departureAirport: 'KLGB',
    destinations: ['KLGB'],
    passengerCount: 0,
    fuelLevelOnReturn: '3/4',
    notes: 'Pattern work, three landings.',
    baseCharge: 180,
    awayFromBaseCharge: 0,
    totalCharge: 180,
    stripeChargeId: 'ch_demo_002',
    createdAt: ts(day(-7, 12, 30)),
  },
  {
    flightId: 'f-003',
    aircraftId: 'N123AB',
    lesseeUid: DEMO_UIDS.brian,
    date: iso(day(-14)),
    hobbsOut: 4208.9,
    hobbsIn: 4213.5,
    tachOut: 3978.5,
    tachIn: 3982.7,
    hoursFlown: 4.6,
    departureAirport: 'KLGB',
    destinations: ['KCMA', 'KSBA', 'KLGB'],
    passengerCount: 2,
    fuelLevelOnReturn: 'Full',
    baseCharge: 460,
    awayFromBaseCharge: 0,
    totalCharge: 460,
    stripeChargeId: 'ch_demo_003',
    createdAt: ts(day(-14, 16, 0)),
  },
];

// ────────────────────────────── Squawks ──────────────────────────────
export const DEMO_SQUAWKS: SquawkDoc[] = [
  {
    squawkId: 's-door',
    aircraftId: 'N123AB',
    reportedBy: DEMO_UIDS.brian,
    reportedByName: `${OWNER_NAME} (owner)`,
    severity: 'should_fix_soon',
    description:
      'Right passenger door is not latching — the latch screen is broken. Maintenance has been notified and we are awaiting the repair part.',
    photos: [],
    dateDiscovered: iso(day(-2)),
    status: 'in_progress',
    adminNotes: 'Maintenance notified; awaiting part. Door must remain secured/placarded until repaired.',
    isGrounding: false,
    createdAt: ts(day(-2, 9, 0)),
  },
  {
    squawkId: 's-003',
    aircraftId: 'N123AB',
    reportedBy: DEMO_UIDS.brian,
    reportedByName: 'Brian Matthews',
    severity: 'should_fix_soon',
    description: 'Landing light intermittent — flickers on rollout.',
    photos: [],
    dateDiscovered: iso(day(-22)),
    status: 'resolved',
    adminNotes: 'Replaced landing light bulb and reseated connector. Tested good.',
    isGrounding: false,
    createdAt: ts(day(-22, 9, 0)),
    resolvedAt: ts(day(-18, 15, 0)),
  },
];

// ────────────────────────────── Transactions ──────────────────────────────
export const DEMO_TRANSACTIONS: TransactionDoc[] = DEMO_FLIGHTS.map((f) => ({
  transactionId: `t-${f.flightId}`,
  lesseeUid: f.lesseeUid,
  aircraftId: f.aircraftId,
  flightId: f.flightId,
  type: 'flight_charge',
  amount: f.totalCharge,
  stripeChargeId: f.stripeChargeId,
  status: 'succeeded',
  createdAt: f.createdAt,
}));

// ────────────────────────────── Documents ──────────────────────────────
export const DEMO_DOCUMENTS: DocumentDoc[] = [
  {
    documentId: 'd-001',
    ownerUid: DEMO_UIDS.brian,
    type: 'medical',
    fileRef: '#',
    fileName: 'medical_brian.pdf',
    expirationDate: iso(day(218)),
    verificationStatus: 'verified',
    uploadedAt: ts(day(-70)),
    verifiedAt: ts(day(-69)),
  },
  {
    documentId: 'd-002',
    ownerUid: DEMO_UIDS.brian,
    type: 'pilot_certificate',
    fileRef: '#',
    fileName: 'pilot_cert.jpg',
    verificationStatus: 'verified',
    uploadedAt: ts(day(-70)),
    verifiedAt: ts(day(-69)),
  },
  {
    documentId: 'd-003',
    ownerUid: DEMO_UIDS.morgan,
    type: 'pilot_certificate',
    fileRef: '#',
    fileName: 'morgan_cert.pdf',
    verificationStatus: 'pending',
    uploadedAt: ts(day(-3)),
  },
  {
    documentId: 'd-004',
    ownerUid: DEMO_UIDS.morgan,
    type: 'medical',
    fileRef: '#',
    fileName: 'morgan_medical.pdf',
    expirationDate: iso(day(165)),
    verificationStatus: 'pending',
    uploadedAt: ts(day(-3)),
  },
];


// ────────────────────────────── Activity feed ──────────────────────────────
export interface ActivityEvent {
  id: string;
  at: Date;
  text: string;
  kind: 'flight' | 'squawk' | 'payment' | 'booking' | 'doc';
}

export const DEMO_ACTIVITY: ActivityEvent[] = [
  { id: 'a1', at: day(-3, 13, 0), text: 'Brian logged a flight (3.3 hrs)', kind: 'flight' },
  { id: 'a2', at: day(-3, 13, 5), text: 'Stripe charge processed ($330)', kind: 'payment' },
  { id: 'a3', at: day(-3, 13, 30), text: 'Brian reported a squawk (should fix soon)', kind: 'squawk' },
  { id: 'a4', at: day(-7, 12, 30), text: 'Jordan logged a flight (1.8 hrs)', kind: 'flight' },
  { id: 'a5', at: day(-7, 12, 35), text: 'Stripe charge processed ($180)', kind: 'payment' },
  { id: 'a6', at: day(0, 18, 0), text: 'New booking request: Brian, 3-day trip', kind: 'booking' },
  { id: 'a7', at: day(-3, 10, 0), text: 'Morgan uploaded medical certificate', kind: 'doc' },
];

export function bookingsForLessee(uid: string) {
  return DEMO_BOOKINGS.filter((b) => b.lesseeUid === uid);
}
export function flightsForLessee(uid: string) {
  return DEMO_FLIGHTS.filter((f) => f.lesseeUid === uid);
}
export function squawksFor(uid: string) {
  return DEMO_SQUAWKS.filter((s) => s.reportedBy === uid);
}
export function txForLessee(uid: string) {
  return DEMO_TRANSACTIONS.filter((t) => t.lesseeUid === uid);
}
export function lesseeByUid(uid: string): UserDoc | undefined {
  return DEMO_LESSEES.find((u) => u.uid === uid);
}
